import React from "react";
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Card,
  CardHeader,
  CardBody,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Switch,
  useToast,
  Alert,
  AlertIcon,
  Progress,
  Stepper,
  Step,
  StepIndicator,
  StepStatus,
  StepIcon,
  StepNumber,
  StepTitle,
  StepDescription,
  StepSeparator,
  useSteps,
  Badge,
  Divider,
  SimpleGrid,
  useColorModeValue,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { ArrowBackIcon, CheckIcon, ExternalLinkIcon, StarIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor, getComponent, brandConfig } from "../../brandConfig";

// GraphQL Operations
const GET_CLIENTS = gql`
  query GetClients {
    clients {
      id
      fName
      lName
      email
      businessName
    }
  }
`;

const CREATE_TENANT = gql`
  mutation CreateTenant($input: TenantInput!) {
    createTenant(input: $input) {
      id
      name
      domain
      websiteUrl
      status
      subscriptionTier
      githubRepo
      githubOwner
      deploymentUrl
      moduleConfig {
        moduleId
        version
        enabled
        enabledAt
      }
      branding {
        siteName
        tagline
        description
        primaryColor
      }
      emailConfig {
        fromEmail
        fromName
      }
      smsConfig {
        defaultSender
        defaultSenderType
      }
      apiKeys {
        postmarkApiKey
        cellcastApiKey
        stripePublicKey
        stripeSecretKey
      }
      createdAt
      updatedAt
      trialEndsAt
    }
  }
`;

const CREATE_CLIENT_REPO = gql`
  mutation CreateClientRepo($clientRepoName: String!, $clientGithubOwner: String!, $clientGithubToken: String!) {
    createClientRepo(clientRepoName: $clientRepoName, clientGithubOwner: $clientGithubOwner, clientGithubToken: $clientGithubToken)
  }
`;

const UPDATE_TENANT = gql`
  mutation UpdateTenant($id: ID!, $input: UpdateTenantInput!) {
    updateTenant(id: $id, input: $input) {
      id
      githubRepo
      githubOwner
      deploymentUrl
    }
  }
`;

const IMPROVE_TEXT = gql`
  mutation ImproveText($text: String!, $type: String!, $context: String) {
    improveText(text: $text, type: $type, context: $context)
  }
`;

const IMPROVE_TAGLINE = gql`
  mutation ImproveTagline($text: String!, $context: String) {
    improveTagline(text: $text, context: $context)
  }
`;

const IMPROVE_DESCRIPTION = gql`
  mutation ImproveDescription($text: String!, $context: String) {
    improveDescription(text: $text, context: $context)
  }
`;

// Form interfaces
interface TenantFormData {
  // Basic Info
  name: string;
  domain: string;
  clientId: string;
  subscriptionTier: "FREE" | "BASIC" | "PREMIUM" | "ENTERPRISE";

  // Branding
  siteName: string;
  tagline: string;
  description: string;
  primaryColor: string;

  // GitHub Config
  githubOwner: string;
  githubToken: string;
  autoCreateRepo: boolean;

  // Email Config
  fromEmail: string;
  fromName: string;

  // SMS Config
  defaultSender: string;

  // API Keys
  postmarkApiKey: string;
  cellcastApiKey: string;
  stripePublicKey: string;
  stripeSecretKey: string;
}

const CreateTenant = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const bg = getColor("background.surface");

  const [formData, setFormData] = React.useState<TenantFormData>({
    name: "",
    domain: "",
    clientId: "",
    subscriptionTier: "BASIC",
    siteName: "",
    tagline: "",
    description: "",
    primaryColor: "#3182CE",
    githubOwner: "",
    githubToken: "",
    autoCreateRepo: true,
    fromEmail: "",
    fromName: "",
    defaultSender: "",
    postmarkApiKey: "",
    cellcastApiKey: "",
    stripePublicKey: "",
    stripeSecretKey: "",
  });

  const [isCreating, setIsCreating] = React.useState(false);
  const [createdTenantId, setCreatedTenantId] = React.useState<string>("");
  const [repoUrl, setRepoUrl] = React.useState<string>("");

  // Claude AI states
  const [isImprovingTagline, setIsImprovingTagline] = React.useState(false);
  const [isImprovingDescription, setIsImprovingDescription] = React.useState(false);

  const steps = [
    { title: "Basic Info", description: "Site name and client" },
    { title: "Branding", description: "Colors and messaging" },
    { title: "GitHub Setup", description: "Repository creation" },
    { title: "Services", description: "Email, SMS & API configuration" },
    { title: "Review", description: "Confirm and create" }
  ];

  const { activeStep, setActiveStep } = useSteps({
    index: 0,
    count: steps.length,
  });

  const { data: clientsData, loading: clientsLoading } = useQuery(GET_CLIENTS);
  const [createTenant] = useMutation(CREATE_TENANT);
  const [createClientRepo] = useMutation(CREATE_CLIENT_REPO);
  const [updateTenant] = useMutation(UPDATE_TENANT);
  const [improveTaglineMutation] = useMutation(IMPROVE_TAGLINE);
  const [improveDescriptionMutation] = useMutation(IMPROVE_DESCRIPTION);

  // Generate repo name from site name
  const generateRepoName = (siteName: string) => {
    return siteName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "") + "-frontend";
  };

  const handleImproveTagline = async () => {
    if (!formData.tagline.trim()) {
      toast({
        title: "No text to improve",
        description: "Please enter a tagline first",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsImprovingTagline(true);
    try {
      const context = formData.siteName ? `Site name: ${formData.siteName}` : undefined;
      
      const { data } = await improveTaglineMutation({
        variables: {
          text: formData.tagline,
          context
        }
      });

      if (data?.improveTagline) {
        handleInputChange('tagline', data.improveTagline);
        toast({
          title: "✨ Tagline improved!",
          description: "Claude has enhanced your tagline",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error improving tagline:', error);
      toast({
        title: "Improvement failed",
        description: error instanceof Error ? error.message : "Failed to improve tagline. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsImprovingTagline(false);
    }
  };

  const handleImproveDescription = async () => {
    if (!formData.description.trim()) {
      toast({
        title: "No text to improve",
        description: "Please enter a description first",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsImprovingDescription(true);
    try {
      const context = formData.siteName ? `Site name: ${formData.siteName}` : undefined;
      
      const { data } = await improveDescriptionMutation({
        variables: {
          text: formData.description,
          context
        }
      });

      if (data?.improveDescription) {
        handleInputChange('description', data.improveDescription);
        toast({
          title: "✨ Description improved!",
          description: "Claude has enhanced your description",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error improving description:', error);
      toast({
        title: "Improvement failed",
        description: error instanceof Error ? error.message : "Failed to improve description. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsImprovingDescription(false);
    }
  };

  const handleInputChange = (field: keyof TenantFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0: // Basic Info
        return !!(formData.name && formData.siteName && formData.clientId);
      case 1: // Branding
        return !!(formData.siteName && formData.primaryColor);
      case 2: // GitHub Setup
        return true; // GitHub fields are optional, backend has defaults
      case 3: // API Keys & SMS Config
        return !!(formData.fromEmail && formData.fromName && formData.defaultSender);
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    } else {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const prevStep = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setIsCreating(true);

    try {
      // Step 1: Create the tenant
      console.log("🏗️ Creating tenant...");
      const tenantResult = await createTenant({
        variables: {
          input: {
            name: formData.name,
            domain: formData.domain || undefined,
            clientId: formData.clientId,
            subscriptionTier: formData.subscriptionTier,
            websiteUrl: undefined, // Will be set after deployment
            branding: {
              siteName: formData.siteName,
              tagline: formData.tagline || undefined,
              description: formData.description || undefined,
              primaryColor: formData.primaryColor,
            },
            emailConfig: {
              fromEmail: formData.fromEmail,
              fromName: formData.fromName,
            },
            smsConfig: {
              defaultSender: formData.defaultSender || "BusinessApp",
              defaultSenderType: "ALPHANUMERIC",
              defaultTags: [],
              defaultList: [],
            },
            apiKeys: {
              postmarkApiKey: formData.postmarkApiKey || undefined,
              cellcastApiKey: formData.cellcastApiKey || undefined,
              stripePublicKey: formData.stripePublicKey || undefined,
              stripeSecretKey: formData.stripeSecretKey || undefined,
            },
            skipGithubRepo: !formData.autoCreateRepo,
            githubRepo: formData.autoCreateRepo ? generateRepoName(formData.siteName) : undefined,
            githubOwner: formData.autoCreateRepo && formData.githubOwner ? formData.githubOwner : undefined,
            githubToken: formData.autoCreateRepo && formData.githubToken ? formData.githubToken : undefined,
            mainBranch: "main"
          }
        }
      });

      const newTenant = tenantResult.data.createTenant;
      setCreatedTenantId(newTenant.id);

      console.log("✅ Tenant created:", newTenant.id);

      // The resolver automatically handles GitHub repo creation and tenant updates
      if (formData.autoCreateRepo) {
        console.log("🚀 GitHub repository creation handled automatically by resolver");
        if (newTenant.deploymentUrl) {
          setRepoUrl(newTenant.deploymentUrl);
          console.log("✅ Repository created:", newTenant.deploymentUrl);
        }
      }

      toast({
        title: "🎉 Site Created Successfully!",
        description: `${formData.siteName} is ready to go!`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Redirect to tenant management after short delay
      setTimeout(() => {
        navigate("/admin/tenants");
      }, 2000);

    } catch (error) {
      console.error("❌ Error creating tenant:", error);
      toast({
        title: "Creation Failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Basic Info
        return (
          <VStack spacing={6} align="stretch">
            <FormControl isRequired>
              <FormLabel
                color={getComponent("form", "labelColor")}
                fontWeight="500"
                fontSize="sm"
                mb={2}
              >
                Site Name
              </FormLabel>
              <Input
                value={formData.siteName}
                onChange={(e) => handleInputChange("siteName", e.target.value)}
                placeholder="Life Essentials Club"
                bg={getComponent("form", "fieldBg")}
                border="1px"
                borderColor={getComponent("form", "fieldBorder")}
                borderRadius="lg"
                boxShadow={getComponent("form", "fieldShadow")}
                _placeholder={{ color: getComponent("form", "placeholderColor") }}
                _focus={{
                  borderColor: getComponent("form", "fieldBorderFocus"),
                  boxShadow: getComponent("form", "fieldShadowFocus"),
                  outline: "none"
                }}
                _hover={{
                  borderColor: getColor("border.medium")
                }}
                size="lg"
                fontFamily={brandConfig.fonts.body}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel
                color={getComponent("form", "labelColor")}
                fontWeight="500"
                fontSize="sm"
                mb={2}
              >
                Internal Name
              </FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="lifeessentialsclub"
                bg={getComponent("form", "fieldBg")}
                border="1px"
                borderColor={getComponent("form", "fieldBorder")}
                borderRadius="lg"
                boxShadow={getComponent("form", "fieldShadow")}
                _placeholder={{ color: getComponent("form", "placeholderColor") }}
                _focus={{
                  borderColor: getComponent("form", "fieldBorderFocus"),
                  boxShadow: getComponent("form", "fieldShadowFocus"),
                  outline: "none"
                }}
                _hover={{
                  borderColor: getColor("border.medium")
                }}
                size="lg"
                fontFamily={brandConfig.fonts.body}
              />
            </FormControl>

            <FormControl>
              <FormLabel
                color={getComponent("form", "labelColor")}
                fontWeight="500"
                fontSize="sm"
                mb={2}
              >
                Domain (Optional)
              </FormLabel>
              <Input
                value={formData.domain}
                onChange={(e) => handleInputChange("domain", e.target.value)}
                placeholder="lifeessentials.club"
                bg={getComponent("form", "fieldBg")}
                border="1px"
                borderColor={getComponent("form", "fieldBorder")}
                borderRadius="lg"
                boxShadow={getComponent("form", "fieldShadow")}
                _placeholder={{ color: getComponent("form", "placeholderColor") }}
                _focus={{
                  borderColor: getComponent("form", "fieldBorderFocus"),
                  boxShadow: getComponent("form", "fieldShadowFocus"),
                  outline: "none"
                }}
                _hover={{
                  borderColor: getColor("border.medium")
                }}
                size="lg"
                fontFamily={brandConfig.fonts.body}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel
                color={getComponent("form", "labelColor")}
                fontWeight="500"
                fontSize="sm"
                mb={2}
              >
                Client Owner
              </FormLabel>
              <Select
                value={formData.clientId}
                onChange={(e) => handleInputChange("clientId", e.target.value)}
                placeholder="Select a client"
                bg={getComponent("form", "fieldBg")}
                border="1px"
                borderColor={getComponent("form", "fieldBorder")}
                borderRadius="lg"
                boxShadow={getComponent("form", "fieldShadow")}
                _focus={{
                  borderColor: getComponent("form", "fieldBorderFocus"),
                  boxShadow: getComponent("form", "fieldShadowFocus"),
                  outline: "none"
                }}
                _hover={{
                  borderColor: getColor("border.medium")
                }}
                size="lg"
                fontFamily={brandConfig.fonts.body}
              >
                {clientsData?.clients.map((client: any) => (
                  <option key={client.id} value={client.id}>
                    {client.businessName || `${client.fName} ${client.lName}`} ({client.email})
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel
                color={getComponent("form", "labelColor")}
                fontWeight="500"
                fontSize="sm"
                mb={2}
              >
                Subscription Tier
              </FormLabel>
              <Select
                value={formData.subscriptionTier}
                onChange={(e) => handleInputChange("subscriptionTier", e.target.value as any)}
                bg={getComponent("form", "fieldBg")}
                border="1px"
                borderColor={getComponent("form", "fieldBorder")}
                borderRadius="lg"
                boxShadow={getComponent("form", "fieldShadow")}
                _focus={{
                  borderColor: getComponent("form", "fieldBorderFocus"),
                  boxShadow: getComponent("form", "fieldShadowFocus"),
                  outline: "none"
                }}
                _hover={{
                  borderColor: getColor("border.medium")
                }}
                size="lg"
                fontFamily={brandConfig.fonts.body}
              >
                <option value="FREE">FREE - Basic features</option>
                <option value="BASIC">BASIC - $297/month</option>
                <option value="PREMIUM">PREMIUM - $597/month</option>
                <option value="ENTERPRISE">ENTERPRISE - $1,497/month</option>
              </Select>
            </FormControl>
          </VStack>
        );

      case 1: // Branding
        return (
          <VStack spacing={6} align="stretch">
            <FormControl>
              <FormLabel
                color={getComponent("form", "labelColor")}
                fontWeight="500"
                fontSize="sm"
                mb={2}
              >
                Tagline
              </FormLabel>
              <HStack spacing={2}>
                <Input
                  value={formData.tagline}
                  onChange={(e) => handleInputChange("tagline", e.target.value)}
                  placeholder="Empowering wellness through natural solutions"
                  bg={getComponent("form", "fieldBg")}
                  border="1px"
                  borderColor={getComponent("form", "fieldBorder")}
                  borderRadius="lg"
                  boxShadow={getComponent("form", "fieldShadow")}
                  _placeholder={{ color: getComponent("form", "placeholderColor") }}
                  _focus={{
                    borderColor: getComponent("form", "fieldBorderFocus"),
                    boxShadow: getComponent("form", "fieldShadowFocus"),
                    outline: "none"
                  }}
                  _hover={{
                    borderColor: getColor("border.medium")
                  }}
                  size="lg"
                  fontFamily={brandConfig.fonts.body}
                />
                <Tooltip label="Improve grammar and phrasing with Claude AI" hasArrow>
                  <Button
                    onClick={handleImproveTagline}
                    isLoading={isImprovingTagline}
                    loadingText="✨"
                    colorScheme="purple"
                    variant="outline"
                    size="lg"
                    minW="auto"
                    px={4}
                    isDisabled={!formData.tagline.trim()}
                  >
                    ✨ AI
                  </Button>
                </Tooltip>
              </HStack>
            </FormControl>

            <FormControl>
              <HStack justify="space-between" align="end" mb={2}>
                <FormLabel
                  color={getComponent("form", "labelColor")}
                  fontWeight="500"
                  fontSize="sm"
                  mb={0}
                >
                  Description
                </FormLabel>
                <Tooltip label="Improve grammar and phrasing with Claude AI" hasArrow>
                  <Button
                    onClick={handleImproveDescription}
                    isLoading={isImprovingDescription}
                    loadingText="✨"
                    colorScheme="purple"
                    variant="outline"
                    size="sm"
                    minW="auto"
                    px={3}
                    isDisabled={!formData.description.trim()}
                  >
                    ✨ AI
                  </Button>
                </Tooltip>
              </HStack>
              <Textarea
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                placeholder="A brief description of what this site offers..."
                rows={4}
                bg={getComponent("form", "fieldBg")}
                border="1px"
                borderColor={getComponent("form", "fieldBorder")}
                borderRadius="lg"
                boxShadow={getComponent("form", "fieldShadow")}
                _placeholder={{ color: getComponent("form", "placeholderColor") }}
                _focus={{
                  borderColor: getComponent("form", "fieldBorderFocus"),
                  boxShadow: getComponent("form", "fieldShadowFocus"),
                  outline: "none"
                }}
                _hover={{
                  borderColor: getColor("border.medium")
                }}
                fontFamily={brandConfig.fonts.body}
                resize="vertical"
              />
            </FormControl>

            <FormControl>
              <FormLabel
                color={getComponent("form", "labelColor")}
                fontWeight="500"
                fontSize="sm"
                mb={2}
              >
                Primary Color
              </FormLabel>
              <HStack>
                <Input
                  type="color"
                  value={formData.primaryColor}
                  onChange={(e) => handleInputChange("primaryColor", e.target.value)}
                  w="100px"
                  bg={getComponent("form", "fieldBg")}
                  border="1px"
                  borderColor={getComponent("form", "fieldBorder")}
                  borderRadius="lg"
                  boxShadow={getComponent("form", "fieldShadow")}
                  _focus={{
                    borderColor: getComponent("form", "fieldBorderFocus"),
                    boxShadow: getComponent("form", "fieldShadowFocus"),
                    outline: "none"
                  }}
                  _hover={{
                    borderColor: getColor("border.medium")
                  }}
                  size="lg"
                />
                <Input
                  value={formData.primaryColor}
                  onChange={(e) => handleInputChange("primaryColor", e.target.value)}
                  placeholder="#3182CE"
                  bg={getComponent("form", "fieldBg")}
                  border="1px"
                  borderColor={getComponent("form", "fieldBorder")}
                  borderRadius="lg"
                  boxShadow={getComponent("form", "fieldShadow")}
                  _placeholder={{ color: getComponent("form", "placeholderColor") }}
                  _focus={{
                    borderColor: getComponent("form", "fieldBorderFocus"),
                    boxShadow: getComponent("form", "fieldShadowFocus"),
                    outline: "none"
                  }}
                  _hover={{
                    borderColor: getColor("border.medium")
                  }}
                  size="lg"
                  fontFamily={brandConfig.fonts.body}
                />
              </HStack>
            </FormControl>

            <Box p={4} border="1px" borderColor="gray.200" borderRadius="md">
              <Text fontWeight="bold" mb={2}>Preview:</Text>
              <Box p={4} bg={formData.primaryColor} color="white" borderRadius="md">
                <Heading size="md">{formData.siteName || "Your Site Name"}</Heading>
                <Text>{formData.tagline || "Your tagline here"}</Text>
              </Box>
            </Box>
          </VStack>
        );

      case 2: // GitHub Setup
        return (
          <VStack spacing={6} align="stretch">
            <FormControl>
              <HStack justify="space-between">
                <FormLabel>Auto-create GitHub Repository</FormLabel>
                <Switch
                  isChecked={formData.autoCreateRepo}
                  onChange={(e) => setFormData(prev => ({ ...prev, autoCreateRepo: e.target.checked }))}
                />
              </HStack>
              <Text fontSize="sm" color="gray.600">
                Creates a copy of the master repository for this client
              </Text>
            </FormControl>

            {formData.autoCreateRepo && (
              <>
                <FormControl>
                  <FormLabel
                    color={getComponent("form", "labelColor")}
                    fontWeight="500"
                    fontSize="sm"
                    mb={2}
                  >
                    GitHub Username/Organization (Optional)
                  </FormLabel>
                  <Input
                    value={formData.githubOwner}
                    onChange={(e) => handleInputChange("githubOwner", e.target.value)}
                    placeholder="your-github-username (defaults to system account)"
                    bg={getComponent("form", "fieldBg")}
                    border="1px"
                    borderColor={getComponent("form", "fieldBorder")}
                    borderRadius="lg"
                    boxShadow={getComponent("form", "fieldShadow")}
                    _placeholder={{ color: getComponent("form", "placeholderColor") }}
                    _focus={{
                      borderColor: getComponent("form", "fieldBorderFocus"),
                      boxShadow: getComponent("form", "fieldShadowFocus"),
                      outline: "none"
                    }}
                    _hover={{
                      borderColor: getColor("border.medium")
                    }}
                    size="lg"
                    fontFamily={brandConfig.fonts.body}
                  />
                  <Text fontSize="sm" color={getColor("text.muted")} mt={1}>
                    Leave empty to use system default GitHub account
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel
                    color={getComponent("form", "labelColor")}
                    fontWeight="500"
                    fontSize="sm"
                    mb={2}
                  >
                    GitHub Personal Access Token (Optional)
                  </FormLabel>
                  <Input
                    type="password"
                    value={formData.githubToken}
                    onChange={(e) => handleInputChange("githubToken", e.target.value)}
                    placeholder="ghp_xxxxxxxxxxxxxxxxxxxx (defaults to system token)"
                    bg={getComponent("form", "fieldBg")}
                    border="1px"
                    borderColor={getComponent("form", "fieldBorder")}
                    borderRadius="lg"
                    boxShadow={getComponent("form", "fieldShadow")}
                    _placeholder={{ color: getComponent("form", "placeholderColor") }}
                    _focus={{
                      borderColor: getComponent("form", "fieldBorderFocus"),
                      boxShadow: getComponent("form", "fieldShadowFocus"),
                      outline: "none"
                    }}
                    _hover={{
                      borderColor: getColor("border.medium")
                    }}
                    size="lg"
                    fontFamily={brandConfig.fonts.body}
                  />
                  <Text fontSize="sm" color={getColor("text.muted")} mt={1}>
                    Leave empty to use system default token. If provided, needs "repo" permissions
                  </Text>
                </FormControl>

                <Alert status="info">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">Repository will be created as:</Text>
                    <Text>
                      {formData.githubOwner || "system-default"}/{generateRepoName(formData.siteName || "your-site")}
                    </Text>
                    {!formData.githubOwner && (
                      <Text fontSize="sm" color={getColor("text.muted")} mt={1}>
                        Using system default GitHub account
                      </Text>
                    )}
                  </Box>
                </Alert>
              </>
            )}
          </VStack>
        );

      case 3: // API Keys
        return (
          <VStack spacing={6} align="stretch">
            <Text fontWeight="bold" color="blue.600">Email Configuration</Text>

            <FormControl isRequired>
              <FormLabel
                color={getComponent("form", "labelColor")}
                fontWeight="500"
                fontSize="sm"
                mb={2}
              >
                From Email
              </FormLabel>
              <Input
                type="email"
                value={formData.fromEmail}
                onChange={(e) => handleInputChange("fromEmail", e.target.value)}
                placeholder="support@lifeessentials.club"
                bg={getComponent("form", "fieldBg")}
                border="1px"
                borderColor={getComponent("form", "fieldBorder")}
                borderRadius="lg"
                boxShadow={getComponent("form", "fieldShadow")}
                _placeholder={{ color: getComponent("form", "placeholderColor") }}
                _focus={{
                  borderColor: getComponent("form", "fieldBorderFocus"),
                  boxShadow: getComponent("form", "fieldShadowFocus"),
                  outline: "none"
                }}
                _hover={{
                  borderColor: getColor("border.medium")
                }}
                size="lg"
                fontFamily={brandConfig.fonts.body}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel
                color={getComponent("form", "labelColor")}
                fontWeight="500"
                fontSize="sm"
                mb={2}
              >
                From Name
              </FormLabel>
              <Input
                value={formData.fromName}
                onChange={(e) => handleInputChange("fromName", e.target.value)}
                placeholder="Life Essentials Club"
                bg={getComponent("form", "fieldBg")}
                border="1px"
                borderColor={getComponent("form", "fieldBorder")}
                borderRadius="lg"
                boxShadow={getComponent("form", "fieldShadow")}
                _placeholder={{ color: getComponent("form", "placeholderColor") }}
                _focus={{
                  borderColor: getComponent("form", "fieldBorderFocus"),
                  boxShadow: getComponent("form", "fieldShadowFocus"),
                  outline: "none"
                }}
                _hover={{
                  borderColor: getColor("border.medium")
                }}
                size="lg"
                fontFamily={brandConfig.fonts.body}
              />
            </FormControl>

            <Divider />

            <Text fontWeight="bold" color={getColor("primary")}>SMS Configuration</Text>

            <FormControl>
              <FormLabel
                color={getComponent("form", "labelColor")}
                fontWeight="500"
                fontSize="sm"
                mb={2}
              >
                SMS Sender Name
              </FormLabel>
              <Input
                value={formData.defaultSender}
                onChange={(e) => handleInputChange("defaultSender", e.target.value)}
                placeholder="YourBusiness"
                bg={getComponent("form", "fieldBg")}
                border="1px"
                borderColor={getComponent("form", "fieldBorder")}
                borderRadius="lg"
                boxShadow={getComponent("form", "fieldShadow")}
                _placeholder={{ color: getComponent("form", "placeholderColor") }}
                _focus={{
                  borderColor: getComponent("form", "fieldBorderFocus"),
                  boxShadow: getComponent("form", "fieldShadowFocus"),
                  outline: "none"
                }}
                _hover={{
                  borderColor: getColor("border.medium")
                }}
                size="lg"
                fontFamily={brandConfig.fonts.body}
              />
              <Text fontSize="sm" color={getColor("text.muted")} mt={1}>
                Name that appears as sender of SMS messages (alphanumeric, max 11 chars)
              </Text>
            </FormControl>

            <Divider />

            <Text fontWeight="bold" color={getColor("primary")}>Service API Keys (Optional)</Text>

            <FormControl>
              <FormLabel
                color={getComponent("form", "labelColor")}
                fontWeight="500"
                fontSize="sm"
                mb={2}
              >
                Postmark API Key
              </FormLabel>
              <Input
                type="password"
                value={formData.postmarkApiKey}
                onChange={(e) => handleInputChange("postmarkApiKey", e.target.value)}
                placeholder="Your Postmark server token"
                bg={getComponent("form", "fieldBg")}
                border="1px"
                borderColor={getComponent("form", "fieldBorder")}
                borderRadius="lg"
                boxShadow={getComponent("form", "fieldShadow")}
                _placeholder={{ color: getComponent("form", "placeholderColor") }}
                _focus={{
                  borderColor: getComponent("form", "fieldBorderFocus"),
                  boxShadow: getComponent("form", "fieldShadowFocus"),
                  outline: "none"
                }}
                _hover={{
                  borderColor: getColor("border.medium")
                }}
                size="lg"
                fontFamily={brandConfig.fonts.body}
              />
            </FormControl>

            <FormControl>
              <FormLabel
                color={getComponent("form", "labelColor")}
                fontWeight="500"
                fontSize="sm"
                mb={2}
              >
                Cellcast API Key
              </FormLabel>
              <Input
                type="password"
                value={formData.cellcastApiKey}
                onChange={(e) => handleInputChange("cellcastApiKey", e.target.value)}
                placeholder="Your Cellcast API key"
                bg={getComponent("form", "fieldBg")}
                border="1px"
                borderColor={getComponent("form", "fieldBorder")}
                borderRadius="lg"
                boxShadow={getComponent("form", "fieldShadow")}
                _placeholder={{ color: getComponent("form", "placeholderColor") }}
                _focus={{
                  borderColor: getComponent("form", "fieldBorderFocus"),
                  boxShadow: getComponent("form", "fieldShadowFocus"),
                  outline: "none"
                }}
                _hover={{
                  borderColor: getColor("border.medium")
                }}
                size="lg"
                fontFamily={brandConfig.fonts.body}
              />
            </FormControl>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel
                  color={getComponent("form", "labelColor")}
                  fontWeight="500"
                  fontSize="sm"
                  mb={2}
                >
                  Stripe Public Key
                </FormLabel>
                <Input
                  value={formData.stripePublicKey}
                  onChange={(e) => handleInputChange("stripePublicKey", e.target.value)}
                  placeholder="pk_live_..."
                  bg={getComponent("form", "fieldBg")}
                  border="1px"
                  borderColor={getComponent("form", "fieldBorder")}
                  borderRadius="lg"
                  boxShadow={getComponent("form", "fieldShadow")}
                  _placeholder={{ color: getComponent("form", "placeholderColor") }}
                  _focus={{
                    borderColor: getComponent("form", "fieldBorderFocus"),
                    boxShadow: getComponent("form", "fieldShadowFocus"),
                    outline: "none"
                  }}
                  _hover={{
                    borderColor: getColor("border.medium")
                  }}
                  size="lg"
                  fontFamily={brandConfig.fonts.body}
                />
              </FormControl>

              <FormControl>
                <FormLabel
                  color={getComponent("form", "labelColor")}
                  fontWeight="500"
                  fontSize="sm"
                  mb={2}
                >
                  Stripe Secret Key
                </FormLabel>
                <Input
                  type="password"
                  value={formData.stripeSecretKey}
                  onChange={(e) => handleInputChange("stripeSecretKey", e.target.value)}
                  placeholder="sk_live_..."
                  bg={getComponent("form", "fieldBg")}
                  border="1px"
                  borderColor={getComponent("form", "fieldBorder")}
                  borderRadius="lg"
                  boxShadow={getComponent("form", "fieldShadow")}
                  _placeholder={{ color: getComponent("form", "placeholderColor") }}
                  _focus={{
                    borderColor: getComponent("form", "fieldBorderFocus"),
                    boxShadow: getComponent("form", "fieldShadowFocus"),
                    outline: "none"
                  }}
                  _hover={{
                    borderColor: getColor("border.medium")
                  }}
                  size="lg"
                  fontFamily={brandConfig.fonts.body}
                />
              </FormControl>
            </SimpleGrid>
          </VStack>
        );

      case 4: // Review
        return (
          <VStack spacing={6} align="stretch">
            <Heading size="md">Review Your Configuration</Heading>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <Card>
                <CardHeader>
                  <Text fontWeight="bold">Basic Info</Text>
                </CardHeader>
                <CardBody>
                  <VStack align="start" spacing={2}>
                    <Text><strong>Site Name:</strong> {formData.siteName}</Text>
                    <Text><strong>Domain:</strong> {formData.domain || "Not set"}</Text>
                    <Text><strong>Tier:</strong> <Badge colorScheme="blue">{formData.subscriptionTier}</Badge></Text>
                  </VStack>
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <Text fontWeight="bold">GitHub</Text>
                </CardHeader>
                <CardBody>
                  {formData.autoCreateRepo ? (
                    <VStack align="start" spacing={2}>
                      <Text><strong>Owner:</strong> {formData.githubOwner || "system-default"}</Text>
                      <Text><strong>Repo:</strong> {generateRepoName(formData.siteName)}</Text>
                      <Text><strong>Token:</strong> {formData.githubToken ? "Custom provided" : "System default"}</Text>
                      <Badge colorScheme="green">Auto-create enabled</Badge>
                    </VStack>
                  ) : (
                    <Badge colorScheme="gray">Manual setup</Badge>
                  )}
                </CardBody>
              </Card>
            </SimpleGrid>

            <Alert status="info">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">What happens next:</Text>
                <VStack align="start" spacing={1} mt={2}>
                  <Text>1. ✅ Create tenant record with default modules</Text>
                  <Text>2. 🔗 Link tenant to client automatically</Text>
                  {formData.autoCreateRepo && (
                    <>
                      <Text>3. 🚀 Auto-create GitHub repository from master template</Text>
                      <Text>4. ⚙️ Configure GitHub deployment settings</Text>
                      <Text>5. 📦 Deploy initial modules based on subscription tier</Text>
                    </>
                  )}
                  <Text>{formData.autoCreateRepo ? '6' : '3'}. 🎉 Site ready for Vercel deployment</Text>
                </VStack>
              </Box>
            </Alert>
          </VStack>
        );

      default:
        return null;
    }
  };

  if (isCreating) {
    return (
      <Box bg={bg} minHeight="100vh" display="flex" flexDirection="column">
        <NavbarWithCallToAction />
        <Container maxW="container.md" py={12} flex="1">
          <VStack spacing={8}>
            <Heading>🏗️ Creating Your Site...</Heading>
            <Progress size="lg" isIndeterminate w="full" />
            <VStack spacing={4}>
              <Text>✅ Creating tenant record...</Text>
              {formData.autoCreateRepo && (
                <>
                  <Text>🚀 Creating GitHub repository...</Text>
                  <Text>🔗 Linking repository to tenant...</Text>
                </>
              )}
              {repoUrl && (
                <Alert status="success">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="bold">Repository created successfully!</Text>
                    <Button
                      as="a"
                      href={repoUrl}
                      target="_blank"
                      size="sm"
                      leftIcon={<ExternalLinkIcon />}
                      mt={2}
                    >
                      View Repository
                    </Button>
                  </Box>
                </Alert>
              )}
            </VStack>
          </VStack>
        </Container>
        <FooterWithFourColumns />
      </Box>
    );
  }

  return (
    <Box bg={bg} minHeight="100vh" display="flex" flexDirection="column">
      <NavbarWithCallToAction />
      <Container maxW="container.lg" py={{ base: 6, md: 12 }} px={{ base: 4, md: 6 }} flex="1">
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <HStack justify="space-between" flexDirection={{ base: "column", md: "row" }} align={{ base: "start", md: "center" }} spacing={{ base: 4, md: 0 }}>
            <VStack align="start" spacing={2} w={{ base: "full", md: "auto" }}>
              <HStack>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/admin/tenants")}
                  leftIcon={<ArrowBackIcon />}
                  size={{ base: "sm", md: "md" }}
                >
                  Back to Tenants
                </Button>
              </HStack>
              <Heading size={{ base: "md", md: "lg" }}>🆕 Create New Site</Heading>
              <Text color="gray.600" fontSize={{ base: "sm", md: "md" }}>Set up a new client site with automatic repository creation</Text>
            </VStack>
          </HStack>

          {/* Stepper */}
          <Stepper index={activeStep} orientation="horizontal" size="sm">
            {steps.map((step, index) => (
              <Step key={index}>
                <StepIndicator>
                  <StepStatus
                    complete={<StepIcon />}
                    incomplete={<StepNumber />}
                    active={<StepNumber />}
                  />
                </StepIndicator>

                <Box flexShrink="0" display={{ base: "none", sm: "block" }}>
                  <StepTitle>{step.title}</StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                </Box>

                <StepSeparator />
              </Step>
            ))}
          </Stepper>

          {/* Form Content */}
          <Card>
            <CardBody>
              {renderStepContent()}
            </CardBody>
          </Card>

          {/* Navigation */}
          <HStack justify="space-between" flexDirection={{ base: "column", sm: "row" }} w="full" spacing={{ base: 4, sm: 0 }}>
            <Button
              onClick={prevStep}
              isDisabled={activeStep === 0}
              variant="outline"
              w={{ base: "full", sm: "auto" }}
              size={{ base: "md", md: "md" }}
            >
              Previous
            </Button>

            <HStack w={{ base: "full", sm: "auto" }}>
              {activeStep < steps.length - 1 ? (
                <Button
                  onClick={nextStep}
                  colorScheme="blue"
                  isDisabled={!validateStep(activeStep)}
                  w={{ base: "full", sm: "auto" }}
                  size={{ base: "md", md: "md" }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  colorScheme="green"
                  leftIcon={<CheckIcon />}
                  isLoading={isCreating}
                  loadingText="Creating..."
                  w={{ base: "full", sm: "auto" }}
                  size={{ base: "md", md: "md" }}
                >
                  Create Site
                </Button>
              )}
            </HStack>
          </HStack>
        </VStack>
      </Container>
      <FooterWithFourColumns />
    </Box>
  );
};

export default CreateTenant;